import React, { useState } from 'react';
import { CircularProgress, Typography, Link, Box } from '@mui/material';
import {
  SaveOutlined as SaveIcon,
  DescriptionOutlined as DescriptionIcon,
  DeleteOutlined as DeleteIcon,
  ImportExportOutlined as ImportIcon,
  SmartToyOutlined as AssistantIcon,
  PhotoLibraryOutlined as GalleryIcon,
} from '@mui/icons-material';
import {
  CreateButton,
  DraftsModal,
  ImportModal,
  AssistantModal,
  GalleryModal,
  CharacterPayload,
} from '@components';
import { PyrenzBlueButton } from '@client/theme';
import { useNavigate } from 'react-router-dom';
import { useCharacterStore } from '@client/store';

interface FormActionsProps {
  onClear: () => void;
  onCreate: () => void;
  onSave: () => void;
  onDelete: () => void;
  saveLoading: boolean;
  loading: boolean;
  onSelectDraft: (draft: CharacterPayload) => void;
  character_update: boolean;
}

export function FormActions({
  onClear,
  onCreate,
  onSave,
  onDelete,
  saveLoading,
  loading,
  onSelectDraft,
  character_update,
}: FormActionsProps) {
  const navigate = useNavigate();
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isAssistantModalOpen, setIsAssistantModalOpen] = useState(false);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [pressed, setPressed] = useState<Record<string, boolean>>({});
  const addGalleryImages = useCharacterStore((state) => state.addGalleryImages);
  const setCharacter = useCharacterStore((state) => state.setCharacter);

  const toggleModal = (modal: string, state: boolean) => {
    switch (modal) {
      case 'draft':
        setIsDraftModalOpen(state);
        break;
      case 'import':
        setIsImportModalOpen(state);
        break;
      case 'assistant':
        setIsAssistantModalOpen(state);
        break;
      case 'gallery':
        setIsGalleryModalOpen(state);
        break;
    }
    setPressed((prev) => ({ ...prev, [modal]: state }));
  };

  const handleApplyCharacter = (characterData: any) => {
    const mappedData: Partial<CharacterPayload> = {
      name: characterData.name || '',
      title: characterData.title || characterData.name || '',
      description: characterData.description || characterData.desc || '',
      persona: characterData.persona || characterData.personality || '',
      model_instructions: characterData.model_instructions || characterData.instructions || '',
      scenario: characterData.scenario || '',
      first_message: Array.isArray(characterData.first_message) 
        ? characterData.first_message 
        : characterData.firstMessage 
          ? [characterData.firstMessage]
          : characterData.greeting 
            ? [characterData.greeting]
            : [],
      message_example: characterData.message_example || characterData.example || '',
      tags: Array.isArray(characterData.tags) 
        ? characterData.tags 
        : typeof characterData.tags === 'string'
          ? characterData.tags.split(',').map((t: string) => t.trim())
          : [],
      gender: characterData.gender || '',
      is_public: characterData.is_public !== undefined ? characterData.is_public : characterData.visibility === 'public',
      is_nsfw: characterData.is_nsfw || false,
      profile_image: characterData.profile_image || characterData.image || '',
      lorebook: characterData.lorebook || '',
      attribute: characterData.attribute || '',
      creator_notes: characterData.creator_notes || characterData.notes || '',
      gallery: Array.isArray(characterData.gallery) 
        ? characterData.gallery 
        : [],
    };

    setCharacter(mappedData);
    toggleModal('assistant', false);
  };

  const handleGuideClick = () => navigate('/Docs');

  const buttonConfig = [
    {
      id: 'clear',
      label: 'Clear',
      icon: null,
      color: 'primary',
      onClick: () => onClear(),
    },
    {
      id: 'save',
      label: saveLoading ? 'Saving...' : 'Save',
      icon: saveLoading ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        <SaveIcon />
      ),
      color: 'primary',
      onClick: () => onSave(),
    },
    {
      id: 'draft',
      label: 'Drafts',
      icon: <DescriptionIcon />,
      color: 'primary',
      onClick: () => toggleModal('draft', true),
    },
    {
      id: 'import',
      label: 'Import',
      icon: <ImportIcon />,
      color: 'primary',
      onClick: () => toggleModal('import', true),
    },
    {
      id: 'assistant',
      label: 'Assistant',
      icon: <AssistantIcon />,
      color: 'primary',
      onClick: () => toggleModal('assistant', true),
    },
    {
      id: 'gallery',
      label: 'Gallery',
      icon: <GalleryIcon />,
      color: 'primary',
      onClick: () => toggleModal('gallery', true),
    },
  ];

  if (character_update) {
    buttonConfig.push({
      id: 'delete',
      label: 'Delete',
      icon: <DeleteIcon />,
      color: 'error',
      onClick: () => onDelete(),
    });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 2,
        mt: 4,
      }}
    >
      {buttonConfig.map((btn) => (
        <PyrenzBlueButton
          key={btn.id}
          variant="contained"
          color={btn.color as any}
          onClick={btn.onClick}
          disabled={
            loading || pressed[btn.id] || (btn.id === 'save' && saveLoading)
          }
          startIcon={btn.icon}
        >
          {btn.label}
        </PyrenzBlueButton>
      ))}

      <CreateButton
        onClick={onCreate}
        character_update={character_update}
        disabled={loading}
      />

      {isDraftModalOpen && (
        <DraftsModal
          onClose={() => toggleModal('draft', false)}
          onSelect={onSelectDraft}
        />
      )}
      {isImportModalOpen && (
        <ImportModal
          open={isImportModalOpen}
          onClose={() => toggleModal('import', false)}
        />
      )}
      {isAssistantModalOpen && (
        <AssistantModal
          open={isAssistantModalOpen}
          onClose={() => toggleModal('assistant', false)}
          onApplyCharacter={handleApplyCharacter}
        />
      )}
      {isGalleryModalOpen && (
        <GalleryModal
          open={isGalleryModalOpen}
          onClose={() => toggleModal('gallery', false)}
          onSubmit={(urls: string[]) => {
            addGalleryImages(urls);
            toggleModal('gallery', false);
          }}
        />
      )}

      <Typography
        variant="body1"
        align="center"
        sx={{ width: '100%', mt: 2, color: 'grey.600' }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={handleGuideClick}
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          Not sure where to start? Check out our starter guide! ദ്ദി(ᵔᗜᵔ)
        </Link>
      </Typography>
    </Box>
  );
}